import Reveal from "../components/Reveal";
import AppShotFrame from "../components/AppShotFrame";
import { SIGN_UP_URL } from "../config";
import AppStoreBadge from "../components/AppStoreBadge";
import { trackCta } from "../hooks/useMarketingAnalytics";
import { Link } from "react-router-dom";

const DOMAINS = ["Clients", "Bookings", "Tasks", "Forms", "Documents", "Contracts", "Invoices", "Calendar"];

export default function Hero() {
  return (
    <section
      data-testid="hero"
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 20% -20%, #FDE9D8 0%, rgba(253,233,216,0) 60%), radial-gradient(900px 500px at 90% -10%, #F2ECDC 0%, rgba(242,236,220,0) 55%), #FAF7F2",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 border border-[#EDE1D0] text-[12px] font-medium text-[#B85C1F] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E8712C]" />
              The business workspace built for virtual assistants
            </div>
          </Reveal>

          <Reveal delay={60}>
            <h1 className="font-display font-extrabold text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.02] tracking-[-0.03em] text-[#1A2A3A] mb-6">
              Your VA business.
              <br />
              <span className="text-[#E8712C]">One organized workspace.</span>
            </h1>
          </Reveal>

          <Reveal delay={120}>
            <p className="text-[17px] sm:text-[18px] text-[#57534E] leading-relaxed max-w-xl mb-8">
              Manage clients, bookings, tasks, forms, documents, contracts,
              invoices and calendars — without jumping between six different
              tools.
            </p>
          </Reveal>

          <Reveal delay={180}>
            <div className="flex flex-wrap gap-3 mb-10">
              <a
                href={SIGN_UP_URL}
                data-testid="hero-cta-primary"
                onClick={() => trackCta("hero_primary")}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white text-[15px] font-semibold transition-colors shadow-[0_10px_25px_-10px_rgba(232,113,44,0.6)]"
              >
                Get started
              </a>
              <Link
                to="/product-tour"
                data-testid="hero-cta-secondary"
                onClick={() => trackCta("hero_secondary_tour")}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#DED4C4] bg-white/70 hover:bg-white text-[#1A2A3A] text-[15px] font-semibold transition-colors"
              >
                Take a product tour
              </Link>
              <AppStoreBadge source="hero" />
            </div>
          </Reveal>

          <Reveal delay={240}>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-[#57534E]">
              {DOMAINS.map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#E8712C]" />
                  {d}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={200} className="relative">
          {/* Illustrative photograph — not a customer testimonial. */}
          <div className="relative pt-[44%] pl-[32%] md:pt-[40%] md:pl-[42%]" data-testid="hero-visual">
            <img
              src="/marketing/hero_va_professional_office.webp"
              alt="Illustrative photo of a virtual assistant working at her home-office desk."
              loading="eager"
              decoding="async"
              data-testid="hero-photo"
              className="absolute left-0 top-0 -translate-y-6 md:-left-[4%] md:-top-[6%] w-[66%] md:w-[68%] aspect-[4/5] object-cover object-[50%_25%] rounded-[22px] sm:rounded-[28px] ring-1 ring-black/5 shadow-[0_30px_60px_-30px_rgba(26,42,58,0.45)]"
            />
            <AppShotFrame
              src="/appstore_ipad_01_dashboard.png"
              alt="ClientDesk VA dashboard showing active clients, today's appointments, open tasks, and outstanding invoices."
              variant="tablet"
              className="rotate-[-1.5deg] relative"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
